import classNames from 'classnames'
import Link from 'next/link'
import { type FC, type ReactNode } from 'react'

type ButtonAsLinkProps = {
  href: string
  onNavigate?: (_event: { preventDefault: () => void }) => void
  className?: string
  smallText?: string | ReactNode
  color?: 'emerald' | 'blue' | 'red' | 'gray' | 'brand'
  variant?: 'solid' | 'outline'
  children: ReactNode
}

const BigButton: FC<ButtonAsLinkProps> = ({
  href,
  onNavigate,
  children,
  className,
  smallText,
  color = 'emerald',
  variant = 'solid',
}) => {
  const buttonStyles = {
    base: 'mx-auto block w-full max-w-xs rounded-xl px-3 py-2 md:px-4 md:py-3.5 text-center font-bold tracking-wide transition-all duration-200 active:scale-[0.98]',
    solid: {
      emerald: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md',
      brand: 'bg-brand-secondary hover:opacity-90 text-white shadow-md',
      blue: 'bg-info hover:bg-blue-600 text-white shadow-md',
      red: 'bg-danger hover:bg-red-600 text-white shadow-md',
      gray: 'bg-surface-strong hover:bg-gray-200 dark:hover:bg-white/15 text-main shadow-sm',
    },
    outline: {
      emerald:
        'bg-main border-2 border-emerald-500 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/10',
      brand:
        'bg-main border-2 border-brand-secondary text-brand-secondary hover:bg-brand-secondary-subtle',
      blue: 'bg-main border-2 border-info text-info hover:bg-info-subtle',
      red: 'bg-main border-2 border-danger text-danger hover:bg-danger-subtle',
      gray: 'bg-main border-2 border-main text-main hover:bg-surface-subtle',
    },
  }

  return (
    <Link
      href={href}
      onNavigate={onNavigate}
      className={classNames(
        buttonStyles.base,
        variant === 'solid' ? buttonStyles.solid[color] : buttonStyles.outline[color],
        className,
      )}
    >
      <span className="text-sm md:text-lg">{children}</span>
      {smallText && (
        <small className="block opacity-90 mt-0.5 md:mt-1 text-xs md:text-sm">
          {smallText}
        </small>
      )}
    </Link>
  )
}

export default BigButton
